import { DndContext, useDraggable, useDroppable } from "@dnd-kit/core";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
} from "@mui/material";
import { PRO_COLORS_MAP } from "src/pages/proColors";
import { CSS } from "@dnd-kit/utilities";
import chukkerbookTheme from "src/chukkerbbokTheme";
interface UnifiedSpreadsheetProps {
  unifiedSpreadsheetData: { [key: string]: string[] };
  setUnifiedSpreadsheetData: (data: { [key: string]: string[] }) => void;
  chukkerNumber: number;
  managerMode: number;
  idMapping: { [key: string]: string };
  chukkerDetails: { [key: string]: { jersey_color: { A: string; B: string } } };
}

function TeamIcon({
  color,
  id,
  translatetestId,
}: {
  color: string;
  id: string;
  translatetestId: string;
}) {
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: "draggable_cell_" + id,
  });
  return (
    <IconButton
      //onClick={handleColorClick}
      size="small"
      ref={setNodeRef}
      data-testid={translatetestId}
      style={{ transform: CSS.Transform.toString(transform) }}
      {...listeners}
      {...attributes}
    >
      <img
        src={PRO_COLORS_MAP[color as keyof typeof PRO_COLORS_MAP]}
        alt="Polo Pro Color"
        //data-testid={translatetestId}
        width={30}
        height={30}
        style={{
          display: "flex",
          marginRight: 8,
        }}
      />
    </IconButton>
  );
}

interface SortableRowProps {
  id: string;
  playerName: string;
  chukkerAssignments: string[];
  chukkerDetails: { [key: string]: { jersey_color: { A: string; B: string } } };
}

function RowPlayer({
  id,
  playerName,
  chukkerAssignments,
  chukkerDetails,
}: SortableRowProps) {
  return (
    <TableRow data-testid={`table-row-${id}`}>
      <TableCell
        sx={{
          backgroundColor: chukkerbookTheme["primary"],
          color: "white",
          position: "sticky",
          left: 0,
          zIndex: 1,
        }}
      >
        {playerName}
      </TableCell>
      {chukkerAssignments.map((assignment, index) => (
        <CellPlayer
          key={index}
          index={index}
          playerName={playerName}
          playerId={id}
          id={`${id}-cell-${index}`}
          assignment={assignment}
          chukkerIndex={index}
          chukkerDetails={chukkerDetails}
        />
      ))}
    </TableRow>
  );
}

interface SortableCellProps {
  id: string;
  assignment: string;
  chukkerIndex: number;
  chukkerDetails: { [key: string]: { jersey_color: { A: string; B: string } } };
  index: number;
  playerName: string;
  playerId: number;
}

function CellPlayer({
  id,
  assignment,
  chukkerIndex,
  chukkerDetails,
  index,
  playerName,
  playerId,
}: SortableCellProps) {
  const { isOver, setNodeRef } = useDroppable({
    id: id,
  });
  const style = {
    color: isOver ? "green" : undefined,
  };
  return (
    <TableCell align="center" ref={setNodeRef} style={style} >
      {assignment && (
        <TeamIcon
          id={id}
          translatetestId={
            "table-" +
            (assignment === "x" ? "A" : "B") +
            "-" +
            playerId +
            "-" +
            index
          }
          color={
            assignment === "x"
              ? chukkerDetails[chukkerIndex + 1]?.jersey_color?.A || "#000"
              : chukkerDetails[chukkerIndex + 1]?.jersey_color?.B || "#000"
          }
        />
      )}
    </TableCell>
  );
}

function UnifiedSpreadsheet({
  unifiedSpreadsheetData,
  setUnifiedSpreadsheetData,
  chukkerNumber,
  managerMode,
  idMapping,
  chukkerDetails,
}: UnifiedSpreadsheetProps) {
  //const [data, setData] = useState(unifiedSpreadsheetData);
  const items = Object.entries(unifiedSpreadsheetData).map(
    ([playerId, assignments]) => ({
      id: playerId,
      name: idMapping[playerId],
      chukkerAssignments: assignments,
    })
  );
  function handleDragEnd(event: any) {
    if (event.over && event.over.id.includes("cell")) {
      console.log(event);
      const dropData = event.over.id.split("-");
      const playerId = dropData[0];
      const destChukker = dropData[2];
      const originalData = event.active.id.split("-");
      const originalChukker = originalData[originalData.length - 1];
      console.log(originalChukker);
      if (unifiedSpreadsheetData[playerId][destChukker] !== "") {
        console.log("INVALID drop");
        return;
      }

      setUnifiedSpreadsheetData((prevData: { [key: string]: string[] }) => {
        const newData = { ...prevData };
        newData[playerId][destChukker] = newData[playerId][originalChukker];
        newData[playerId][originalChukker] = "";
        return newData;
      });
    }
  }
  return (
    <TableContainer component={Paper} data-testid="unified-spreadsheet">
      <Table>
        <TableHead>
          <TableRow>
            <TableCell
              sx={{
                backgroundColor: chukkerbookTheme["primary"],
                color: "white",
                position: "sticky",
                left: 0,
                zIndex: 1,
              }}
            >
              Player
            </TableCell>
            {Array.from({ length: chukkerNumber }, (_, i) => (
              <TableCell
                key={i + 1}
                align="center"
                sx={{
                  backgroundColor: chukkerbookTheme["primary"],
                  color: "white",
                }}
              >
                Chukker {i + 1}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <DndContext onDragEnd={handleDragEnd}>
          <TableBody>
            {items.map((item) => (
              <RowPlayer
                key={item.id}
                id={item.id}
                playerName={item.name}
                chukkerAssignments={item.chukkerAssignments}
                chukkerDetails={chukkerDetails}
              />
            ))}
          </TableBody>
        </DndContext>
      </Table>
    </TableContainer>
  );
}

export default UnifiedSpreadsheet;
